/** @file Lambda関数を呼び出す関数を定義するファイル */
import {
  BatchProcessor,
  ERROR_CODE,
  type FetchData,
  Functions,
  UniversalError,
  parse,
  serialize,
  t,
  toUniversalError,
} from "@packages/core";
import { fetchAuthSession } from "aws-amplify/auth";
import { zip } from "es-toolkit";
import { filter, from, mergeMap } from "rxjs";
import * as v from "valibot";
import { ENVIRONMENT_VARIABLE } from "../environment-variable";

/**
 * AWS Lambdaの関数を呼び出す。
 *
 * @param name 関数名
 * @param requestDataSchema リクエストデータのスキーマ関数
 * @param responseDataSchema レスポンスデータのスキーマ関数
 * @param requestData リクエストデータ
 * @param region 地域
 * @param options オプション
 * @returns レスポンスデータ
 */
async function callFunction<
  RequestDataSchema extends v.GenericSchema,
  ResponseDataSchema extends v.GenericSchema,
>(
  {
    endPoint,
    entryPoint = ENVIRONMENT_VARIABLE.FUNCTION_ENTRY_POINT,
    method,
    requestDataSchema,
    responseDataSchema,
  }: {
    method: "DELETE" | "GET" | "POST" | "PUT";
    endPoint: string;
    entryPoint?: string;
    requestDataSchema: RequestDataSchema;
    responseDataSchema: ResponseDataSchema;
  },
  rawRequestData: v.InferInput<RequestDataSchema>,
): Promise<v.InferOutput<ResponseDataSchema>> {
  const url = new URL(entryPoint + endPoint);

  const headers = new Headers();
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens !== undefined) {
      headers.set("Authorization", "Bearer " + tokens.accessToken.toString());
    }
  } catch (error) {
    console.error("認証セッションの取得に失敗しました。", error);
    throw toUniversalError(error, {
      code: ERROR_CODE.AUTHENTICATION_SESSION_ERROR,
      message: t({ ja: "認証セッションの取得に失敗しました。" }),
    });
  }

  let requestData;
  try {
    requestData = parse(requestDataSchema, rawRequestData);
  } catch (error) {
    console.error("リクエストデータの検証に失敗しました。", error);
    throw toUniversalError(error, {
      code: ERROR_CODE.INVALID_DATA,
      message: t({ ja: "リクエストデータの検証に失敗しました。" }),
    });
  }

  let body;
  try {
    body = serialize(requestData);
    if (method === "GET") {
      if (
        typeof body === "string" ||
        typeof body === "number" ||
        typeof body === "boolean" ||
        body === null ||
        Array.isArray(body)
      ) {
        url.search = new URLSearchParams([[".", JSON.stringify(body)]]).toString();
      } else {
        url.search = new URLSearchParams(
          Object.fromEntries(
            Object.entries(body as any)
              .filter(function ([, value]) {
                return value !== undefined;
              })
              .map(function ([key, value]) {
                return [key, JSON.stringify(value)];
              }),
          ),
        ).toString();
      }
    } else {
      headers.set("Content-Type", "application/json");
    }
  } catch (error) {
    console.error("リクエストデータのシリアライズに失敗しました。", error);
    throw toUniversalError(error, {
      code: ERROR_CODE.INVALID_DATA,
      message: t({ ja: "リクエストデータのシリアライズに失敗しました。" }),
    });
  }

  let response;
  try {
    response = await fetch(url, {
      headers,
      method,
      ...(method !== "GET" && { body: JSON.stringify(body) }),
    });
  } catch (error: any) {
    console.error(error);
    throw toUniversalError(error, {
      message: t({ ja: "リクエストに失敗しました。" }),
    });
  }

  let responseData;
  try {
    const characters = await response.text();
    responseData = characters ? JSON.parse(characters) : undefined;
  } catch (error) {
    throw new UniversalError(ERROR_CODE.INVALID_RESPONSE_PACKET, { cause: error });
  }

  if (!response.ok) {
    if (responseData.code) {
      throw UniversalError.fromObject(responseData);
    } else if (responseData.message && typeof responseData.message === "string") {
      throw new UniversalError(ERROR_CODE.UNKNOWN, { message: responseData.message });
    } else {
      console.warn("エラーレスポンスデータが想定していない形式です。", responseData);
      throw new UniversalError(ERROR_CODE.UNEXPECTED, {
        message: t({ ja: "エラーレスポンスデータが想定していない形式です。" }),
      });
    }
  }

  try {
    return v.parse(responseDataSchema, responseData);
  } catch (error) {
    console.error(error);
    throw new UniversalError(ERROR_CODE.INVALID_RESPONSE_PAYLOAD, {
      cause: error,
      message: t({ ja: "レスポンスデータが想定していない形式です。" }),
    });
  }
}

// =============================================================
// 関数群
// =============================================================

export async function allocateBed(
  parameters: v.InferInput<ReturnType<typeof Functions.AllocateBed.getRequestDataSchema>>,
) {
  return await callFunction(
    {
      endPoint: Functions.AllocateBed.END_POINT,
      method: Functions.AllocateBed.METHOD,
      requestDataSchema: Functions.AllocateBed.getRequestDataSchema(),
      responseDataSchema: Functions.AllocateBed.getResponseDataSchema(),
    },
    parameters,
  );
}

export async function batchData(
  parameters: v.InferInput<ReturnType<typeof Functions.BatchData.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.BatchData.END_POINT,
      method: Functions.BatchData.METHOD,
      requestDataSchema: Functions.BatchData.RequestDataSchema(),
      responseDataSchema: Functions.BatchData.ResponseDataSchema(),
    },
    parameters,
  );
}

/**
 * 複数のデータを一括取得するための関数を初期化する。
 *
 * @param options オプション
 * @returns 関数
 */
export function initializeFetchData(
  options?: Partial<{
    batchSize: number;
    debounceTime: number;
    onError?: { (error: unknown): void };
    logger: typeof console;
  }>,
): FetchData {
  if (options?.debounceTime !== undefined && options.debounceTime < 0) {
    throw new UniversalError(
      ERROR_CODE.UNEXPECTED,
      "`debounceTime`には0以上の値を指定してください。",
    );
  }

  if (
    options?.batchSize !== undefined &&
    (!Number.isSafeInteger(options.batchSize) || options.batchSize < 1)
  ) {
    throw new UniversalError(ERROR_CODE.UNEXPECTED, "`batchSize`には1以上の値を指定してください。");
  }

  const rejects = new Set<{ (error: any): void }>();
  function rejectAll(error: any) {
    for (const reject of rejects) {
      reject(error);
    }
  }

  const batchProcessor = new BatchProcessor<
    Parameters<FetchData>[0] & {
      reject: { (error: any): void };
      resolve: { (data: ReturnType<FetchData>): void };
    }
  >(options?.debounceTime ?? 80, options?.batchSize ?? 20);
  const subscription = batchProcessor
    .asObservable()
    .pipe(
      mergeMap(async function (items) {
        try {
          options?.logger?.debug("データを一括取得します。", items);

          const outputs = await batchData(items);

          return zip(items, outputs).map(function ([{ reject, resolve }, response]) {
            return { ...response, reject, resolve };
          });
        } catch (error) {
          options?.onError?.(error);
          return;
        }
      }),
      filter(function (items) {
        return items !== undefined;
      }),
      mergeMap(function (items) {
        return from(items);
      }),
    )
    .subscribe({
      complete() {
        const error = new UniversalError(
          ERROR_CODE.UNEXPECTED,
          "エンティティ一括取得のストリームが意図せず完了しました。",
        );
        rejectAll(error);
        options?.onError?.(error);
      },
      error(error) {
        const universalError = toUniversalError(error, {
          message: "エンティティ一括取得のストリームでエラーが発生しました。",
        });
        rejectAll(universalError);
        options?.onError?.(universalError);
      },
      next({ reject, resolve, ...response }) {
        rejects.delete(reject);
        resolve(response);
      },
    });

  async function fetchData(parameters: Parameters<FetchData>[0]) {
    const { promise, reject, resolve } = Promise.withResolvers<ReturnType<FetchData>>();
    rejects.add(reject);
    batchProcessor.add({ ...parameters, reject, resolve });
    return promise;
  }

  fetchData.finalize = function () {
    const error = new UniversalError(ERROR_CODE.ABORTED, "エンティティ一括取得が中断されました。");
    rejectAll(error);
    subscription.unsubscribe();
  };

  return fetchData as any;
}

/**
 * データを取得する。
 * この関数は自動的にバッチ処理され、一定時間内に要求された複数のデータを一括で取得する。
 *
 * @param parameters パラメーター
 * @returns データ
 */
export const fetchData = initializeFetchData({
  batchSize: 20,
  debounceTime: 80,
  logger: console,
});
// グローバル関数として扱うため、`finalize()`を無効化する。
fetchData.finalize = function () {};

export async function confirmStatus(
  parameters: v.InferInput<ReturnType<typeof Functions.ConfirmStatus.RequestDataSchema>>,
) {
  return await callFunction(
    {
      endPoint: Functions.ConfirmStatus.END_POINT,
      method: Functions.ConfirmStatus.METHOD,
      requestDataSchema: Functions.ConfirmStatus.RequestDataSchema(),
      responseDataSchema: Functions.ConfirmStatus.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function createFacility(
  parameters: v.InferInput<ReturnType<typeof Functions.CreateFacility.getRequestDataSchema>>,
) {
  return await callFunction(
    {
      endPoint: Functions.CreateFacility.END_POINT,
      method: Functions.CreateFacility.METHOD,
      requestDataSchema: Functions.CreateFacility.getRequestDataSchema(),
      responseDataSchema: Functions.CreateFacility.getResponseDataSchema(),
    },
    parameters,
  );
}

export async function createOrganization(
  parameters: v.InferInput<ReturnType<typeof Functions.CreateOrganization.getRequestDataSchema>>,
) {
  return await callFunction(
    {
      endPoint: Functions.CreateOrganization.END_POINT,
      method: Functions.CreateOrganization.METHOD,
      requestDataSchema: Functions.CreateOrganization.getRequestDataSchema(),
      responseDataSchema: Functions.CreateOrganization.getResponseDataSchema(),
    },
    parameters,
  );
}

export async function createProvider(
  parameters: v.InferInput<ReturnType<typeof Functions.CreateProvider.getRequestDataSchema>>,
) {
  return await callFunction(
    {
      endPoint: Functions.CreateProvider.END_POINT,
      method: Functions.CreateProvider.METHOD,
      requestDataSchema: Functions.CreateProvider.getRequestDataSchema(),
      responseDataSchema: Functions.CreateProvider.getResponseDataSchema(),
    },
    parameters,
  );
}

export async function createResident(
  parameters: v.InferInput<ReturnType<typeof Functions.CreateResident.getRequestDataSchema>>,
) {
  return await callFunction(
    {
      endPoint: Functions.CreateResident.END_POINT,
      method: Functions.CreateResident.METHOD,
      requestDataSchema: Functions.CreateResident.getRequestDataSchema(),
      responseDataSchema: Functions.CreateResident.getResponseDataSchema(),
    },
    parameters,
  );
}

export async function createSensor(
  parameters: v.InferInput<ReturnType<typeof Functions.CreateSensor.getRequestDataSchema>>,
) {
  return await callFunction(
    {
      endPoint: Functions.CreateSensor.END_POINT,
      method: Functions.CreateSensor.METHOD,
      requestDataSchema: Functions.CreateSensor.getRequestDataSchema(),
      responseDataSchema: Functions.CreateSensor.getResponseDataSchema(),
    },
    parameters,
  );
}

export async function deleteResidentImage(
  parameters: v.InferInput<ReturnType<typeof Functions.DeleteResidentImage.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.DeleteResidentImage.END_POINT,
      method: Functions.DeleteResidentImage.METHOD,
      requestDataSchema: Functions.DeleteResidentImage.RequestDataSchema(),
      responseDataSchema: Functions.DeleteResidentImage.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function findBeds(
  parameters: v.InferInput<ReturnType<typeof Functions.FindBeds.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.FindBeds.END_POINT,
      method: Functions.FindBeds.METHOD,
      requestDataSchema: Functions.FindBeds.RequestDataSchema(),
      responseDataSchema: Functions.FindBeds.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function findFacilities(
  parameters: v.InferInput<ReturnType<typeof Functions.FindFacilities.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.FindFacilities.END_POINT,
      method: Functions.FindFacilities.METHOD,
      requestDataSchema: Functions.FindFacilities.RequestDataSchema(),
      responseDataSchema: Functions.FindFacilities.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function findFallRiskEntries(
  parameters: v.InferInput<ReturnType<typeof Functions.FindFallRiskEntries.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.FindFallRiskEntries.END_POINT,
      method: Functions.FindFallRiskEntries.METHOD,
      requestDataSchema: Functions.FindFallRiskEntries.RequestDataSchema(),
      responseDataSchema: Functions.FindFallRiskEntries.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function findFallScoreEntries(
  parameters: v.InferInput<ReturnType<typeof Functions.FindFallScoreEntries.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.FindFallScoreEntries.END_POINT,
      method: Functions.FindFallScoreEntries.METHOD,
      requestDataSchema: Functions.FindFallScoreEntries.RequestDataSchema(),
      responseDataSchema: Functions.FindFallScoreEntries.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function findGroups(
  parameters: v.InferInput<ReturnType<typeof Functions.FindGroups.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.FindGroups.END_POINT,
      method: Functions.FindGroups.METHOD,
      requestDataSchema: Functions.FindGroups.RequestDataSchema(),
      responseDataSchema: Functions.FindGroups.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function findInstallations(
  parameters: v.InferInput<ReturnType<typeof Functions.FindInstallations.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.FindInstallations.END_POINT,
      method: Functions.FindInstallations.METHOD,
      requestDataSchema: Functions.FindInstallations.RequestDataSchema(),
      responseDataSchema: Functions.FindInstallations.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function findIndependenceLevelEntries(
  parameters: v.InferInput<
    ReturnType<typeof Functions.FindIndependenceLevelEntries.RequestDataSchema>
  >,
) {
  return callFunction(
    {
      endPoint: Functions.FindIndependenceLevelEntries.END_POINT,
      method: Functions.FindIndependenceLevelEntries.METHOD,
      requestDataSchema: Functions.FindIndependenceLevelEntries.RequestDataSchema(),
      responseDataSchema: Functions.FindIndependenceLevelEntries.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function findOrganizations(
  parameters: v.InferInput<ReturnType<typeof Functions.FindOrganizations.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.FindOrganizations.END_POINT,
      method: Functions.FindOrganizations.METHOD,
      requestDataSchema: Functions.FindOrganizations.RequestDataSchema(),
      responseDataSchema: Functions.FindOrganizations.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function findProviders(
  parameters: v.InferInput<ReturnType<typeof Functions.FindProviders.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.FindProviders.END_POINT,
      method: Functions.FindProviders.METHOD,
      requestDataSchema: Functions.FindProviders.RequestDataSchema(),
      responseDataSchema: Functions.FindProviders.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function findResidents(
  parameters: v.InferInput<ReturnType<typeof Functions.FindResidents.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.FindResidents.END_POINT,
      method: Functions.FindResidents.METHOD,
      requestDataSchema: Functions.FindResidents.RequestDataSchema(),
      responseDataSchema: Functions.FindResidents.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function findSensors(
  parameters: v.InferInput<ReturnType<typeof Functions.FindSensors.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.FindSensors.END_POINT,
      method: Functions.FindSensors.METHOD,
      requestDataSchema: Functions.FindSensors.RequestDataSchema(),
      responseDataSchema: Functions.FindSensors.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function findStatuses(
  parameters: v.InferInput<ReturnType<typeof Functions.FindStatuses.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.FindStatuses.END_POINT,
      method: Functions.FindStatuses.METHOD,
      requestDataSchema: Functions.FindStatuses.RequestDataSchema(),
      responseDataSchema: Functions.FindStatuses.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function generateResidentImageGetUrl(
  parameters: v.InferInput<
    ReturnType<typeof Functions.GenerateResidentImageGetUrl.RequestDataSchema>
  >,
) {
  return callFunction(
    {
      endPoint: Functions.GenerateResidentImageGetUrl.END_POINT,
      method: Functions.GenerateResidentImageGetUrl.METHOD,
      requestDataSchema: Functions.GenerateResidentImageGetUrl.RequestDataSchema(),
      responseDataSchema: Functions.GenerateResidentImageGetUrl.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function generateResidentImagePutUrl(
  parameters: v.InferInput<
    ReturnType<typeof Functions.GenerateResidentImagePutUrl.RequestDataSchema>
  >,
) {
  return callFunction(
    {
      endPoint: Functions.GenerateResidentImagePutUrl.END_POINT,
      method: Functions.GenerateResidentImagePutUrl.METHOD,
      requestDataSchema: Functions.GenerateResidentImagePutUrl.RequestDataSchema(),
      responseDataSchema: Functions.GenerateResidentImagePutUrl.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function modifyBed(
  parameters: v.InferInput<ReturnType<typeof Functions.ModifyBed.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.ModifyBed.END_POINT,
      method: Functions.ModifyBed.METHOD,
      requestDataSchema: Functions.ModifyBed.RequestDataSchema(),
      responseDataSchema: Functions.ModifyBed.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function modifyFacility(
  parameters: v.InferInput<ReturnType<typeof Functions.ModifyFacility.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.ModifyFacility.END_POINT,
      method: Functions.ModifyFacility.METHOD,
      requestDataSchema: Functions.ModifyFacility.RequestDataSchema(),
      responseDataSchema: Functions.ModifyFacility.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function modifyFacilityStaff(
  parameters: v.InferInput<ReturnType<typeof Functions.ModifyFacilityStaff.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.ModifyFacilityStaff.END_POINT,
      method: Functions.ModifyFacilityStaff.METHOD,
      requestDataSchema: Functions.ModifyFacilityStaff.RequestDataSchema(),
      responseDataSchema: Functions.ModifyFacilityStaff.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function modifyGroup(
  parameters: v.InferInput<ReturnType<typeof Functions.ModifyGroup.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.ModifyGroup.END_POINT,
      method: Functions.ModifyGroup.METHOD,
      requestDataSchema: Functions.ModifyGroup.RequestDataSchema(),
      responseDataSchema: Functions.ModifyGroup.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function modifyResident(
  parameters: v.InferInput<ReturnType<typeof Functions.ModifyResident.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.ModifyResident.END_POINT,
      method: Functions.ModifyResident.METHOD,
      requestDataSchema: Functions.ModifyResident.RequestDataSchema(),
      responseDataSchema: Functions.ModifyResident.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function modifyRoom(
  parameters: v.InferInput<ReturnType<typeof Functions.ModifyRoom.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.ModifyRoom.END_POINT,
      method: Functions.ModifyRoom.METHOD,
      requestDataSchema: Functions.ModifyRoom.RequestDataSchema(),
      responseDataSchema: Functions.ModifyRoom.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function modifySensor(
  parameters: v.InferInput<ReturnType<typeof Functions.ModifySensor.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.ModifySensor.END_POINT,
      method: Functions.ModifySensor.METHOD,
      requestDataSchema: Functions.ModifySensor.RequestDataSchema(),
      responseDataSchema: Functions.ModifySensor.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function modifyUser(
  parameters: v.InferInput<ReturnType<typeof Functions.ModifyUser.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.ModifyUser.END_POINT,
      method: Functions.ModifyUser.METHOD,
      requestDataSchema: Functions.ModifyUser.RequestDataSchema(),
      responseDataSchema: Functions.ModifyUser.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function modifyWatchtower(
  parameters: v.InferInput<ReturnType<typeof Functions.ModifyWatchtower.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.ModifyWatchtower.END_POINT,
      method: Functions.ModifyWatchtower.METHOD,
      requestDataSchema: Functions.ModifyWatchtower.RequestDataSchema(),
      responseDataSchema: Functions.ModifyWatchtower.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function registerBed(
  parameters: v.InferInput<ReturnType<typeof Functions.RegisterBed.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.RegisterBed.END_POINT,
      method: Functions.RegisterBed.METHOD,
      requestDataSchema: Functions.RegisterBed.RequestDataSchema(),
      responseDataSchema: Functions.RegisterBed.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function registerFacilityStaff(
  parameters: v.InferInput<ReturnType<typeof Functions.RegisterFacilityStaff.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.RegisterFacilityStaff.END_POINT,
      method: Functions.RegisterFacilityStaff.METHOD,
      requestDataSchema: Functions.RegisterFacilityStaff.RequestDataSchema(),
      responseDataSchema: Functions.RegisterFacilityStaff.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function registerFall(
  parameters: v.InferInput<ReturnType<typeof Functions.RegisterFall.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.RegisterFall.END_POINT,
      method: Functions.RegisterFall.METHOD,
      requestDataSchema: Functions.RegisterFall.RequestDataSchema(),
      responseDataSchema: Functions.RegisterFall.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function registerGroup(
  parameters: v.InferInput<ReturnType<typeof Functions.RegisterGroup.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.RegisterGroup.END_POINT,
      method: Functions.RegisterGroup.METHOD,
      requestDataSchema: Functions.RegisterGroup.RequestDataSchema(),
      responseDataSchema: Functions.RegisterGroup.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function registerInstallation(
  parameters: v.InferInput<ReturnType<typeof Functions.RegisterInstallation.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.RegisterInstallation.END_POINT,
      method: Functions.RegisterInstallation.METHOD,
      requestDataSchema: Functions.RegisterInstallation.RequestDataSchema(),
      responseDataSchema: Functions.RegisterInstallation.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function registerIndependenceLevels(
  parameters: v.InferInput<
    ReturnType<typeof Functions.RegisterIndependenceLevels.RequestDataSchema>
  >,
) {
  return callFunction(
    {
      endPoint: Functions.RegisterIndependenceLevels.END_POINT,
      method: Functions.RegisterIndependenceLevels.METHOD,
      requestDataSchema: Functions.RegisterIndependenceLevels.RequestDataSchema(),
      responseDataSchema: Functions.RegisterIndependenceLevels.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function registerWatchtower(
  parameters: v.InferInput<ReturnType<typeof Functions.RegisterWatchtower.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.RegisterWatchtower.END_POINT,
      method: Functions.RegisterWatchtower.METHOD,
      requestDataSchema: Functions.RegisterWatchtower.RequestDataSchema(),
      responseDataSchema: Functions.RegisterWatchtower.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function subscribePushNotifications(
  parameters: v.InferInput<
    ReturnType<typeof Functions.SubscribePushNotifications.RequestDataSchema>
  >,
) {
  return callFunction(
    {
      endPoint: Functions.SubscribePushNotifications.END_POINT,
      method: Functions.SubscribePushNotifications.METHOD,
      requestDataSchema: Functions.SubscribePushNotifications.RequestDataSchema(),
      responseDataSchema: Functions.SubscribePushNotifications.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function unregisterFall(
  parameters: v.InferInput<ReturnType<typeof Functions.UnregisterFall.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.UnregisterFall.END_POINT,
      method: Functions.UnregisterFall.METHOD,
      requestDataSchema: Functions.UnregisterFall.RequestDataSchema(),
      responseDataSchema: Functions.UnregisterFall.ResponseDataSchema(),
    },
    parameters,
  );
}

export async function verifyWatchtower(
  parameters: v.InferInput<ReturnType<typeof Functions.VerifyWatchtower.RequestDataSchema>>,
) {
  return callFunction(
    {
      endPoint: Functions.VerifyWatchtower.END_POINT,
      method: Functions.VerifyWatchtower.METHOD,
      requestDataSchema: Functions.VerifyWatchtower.RequestDataSchema(),
      responseDataSchema: Functions.VerifyWatchtower.ResponseDataSchema(),
    },
    parameters,
  );
}
